import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import StepIndicator from "@/components/wizard/StepIndicator";
import Step1StyleStory from "@/components/wizard/Step1StyleStory";
import Step2DurationLyrics from "@/components/wizard/Step2DurationLyrics";
import Step3VoiceVisual from "@/components/wizard/Step3VoiceVisual";
import Step4Personalization from "@/components/wizard/Step4Personalization";
import Step5Summary from "@/components/wizard/Step5Summary";
import PriceSidebar from "@/components/wizard/PriceSidebar";
import PurchaseStepsReassurance from "@/components/wizard/PurchaseStepsReassurance";
import GuestCheckoutGate from "@/components/wizard/GuestCheckoutGate";
import { DEFAULT_FORM, calculatePrice } from "@/utils/priceCalculator";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";
import { trackEvent } from "@/utils/analytics";
import { translateApiError } from "@/utils/apiErrors";
import { WIZARD } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

const TOTAL_STEPS = 5;
const STEP_NAMES = ["style_occasion", "duration_lyrics", "voice_visual", "personalization", "review"];

export default function Wizard() {
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem("ams_wizard_draft");
      return saved ? { ...DEFAULT_FORM, ...JSON.parse(saved) } : DEFAULT_FORM;
    } catch (e) {
      return DEFAULT_FORM;
    }
  });
  const [step, setStep] = useState(() => {
    // Resumes exactly where the visitor left off (e.g. after being sent to sign up at the final
    // step) instead of restarting the whole wizard from step 0.
    const saved = parseInt(localStorage.getItem("ams_wizard_step"), 10);
    return Number.isInteger(saved) && saved >= 0 && saved < TOTAL_STEPS ? saved : 0;
  });
  const [pricingConfig, setPricingConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showGuestGate, setShowGuestGate] = useState(false);
  const [guestGateError, setGuestGateError] = useState("");
  const { user, refetch } = useAuth();
  const { t, language } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricingConfig(data));
    refetch();
  }, []);

  useEffect(() => {
    localStorage.setItem("ams_wizard_draft", JSON.stringify(form));
  }, [form]);

  useEffect(() => {
    localStorage.setItem("ams_wizard_step", String(step));
    if (step === TOTAL_STEPS - 1) {
      trackEvent("wizard_reached_final_step", { logged_in: !!user });
    }
  }, [step]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pricing = calculatePrice(form, pricingConfig);

  const validateStep = () => {
    if (step === 1 && form.lyrics_mode === "custom" && !form.custom_lyrics.trim()) {
      toast({ variant: "destructive", title: t("common.error"), description: t("wizard.customLyricsPlaceholder") });
      return false;
    }
    if (step === 2 && form.title_mode === "custom" && !form.custom_title.trim()) {
      toast({ variant: "destructive", title: t("common.error") });
      return false;
    }
    if (step === 3 && form.gift_enabled && !form.gift_email.trim()) {
      toast({ variant: "destructive", title: t("common.error") });
      return false;
    }
    if (step === 3 && form.personal_voice_message && !(form.voice_message_text || "").trim()) {
      toast({ variant: "destructive", title: t("common.error"), description: t("wizard.voiceMessageTextRequired") });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    trackEvent("wizard_step_completed", { step_number: step + 1, step_name: STEP_NAMES[step], step_total: TOTAL_STEPS });
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
  };
  const handlePrev = () => {
    if (step > 0) {
      trackEvent("wizard_back", { step_number: step + 1, step_name: STEP_NAMES[step] });
      setStep(step - 1);
    }
  };

  // Funnel wizard : vue de chaque étape (+ wizard_started sur la 1ère) pour localiser
  // précisément où les visiteurs décrochent (architecture de mesure 18/08/2026).
  useEffect(() => {
    if (step === 0) trackEvent("wizard_started", { step_total: TOTAL_STEPS });
    trackEvent("wizard_step_view", { step_number: step + 1, step_name: STEP_NAMES[step], step_total: TOTAL_STEPS });
  }, [step]);

  const createOrder = async () => {
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", form);
      localStorage.removeItem("ams_wizard_draft");
      localStorage.removeItem("ams_wizard_step");
      trackEvent("order_created", {
        order_id: order.order_id, transaction_id: order.order_id,
        value: order.pricing?.total, currency: "EUR",
        product_type: "song", product_name: form.style,
        quantity: form.num_versions || 1,
      });
      trackEvent("wizard_completed", { step_total: TOTAL_STEPS });
      navigate(`/checkout/${order.order_id}`);
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!user) {
      // The wizard draft (form + step) is already saved in localStorage, so the visitor picks up
      // exactly where they left off once authenticated - this key is the reliable cross-flow
      // signal (unlike router state, it survives the full-page redirect used by Google OAuth).
      localStorage.setItem("ams_post_auth_redirect", "/create");
      trackEvent("signup_wall_shown", { source: "wizard_final_step" });
      setGuestGateError("");
      setShowGuestGate(true);
      return;
    }
    createOrder();
  };

  const handleLoginInstead = () => {
    setShowGuestGate(false);
    navigate("/login", { state: { from: { pathname: "/create" } } });
  };

  const steps = [
    <Step1StyleStory form={form} update={update} />,
    <Step2DurationLyrics form={form} update={update} pricingConfig={pricingConfig} />,
    <Step3VoiceVisual form={form} update={update} pricingConfig={pricingConfig} />,
    <Step4Personalization form={form} update={update} pricingConfig={pricingConfig} />,
    <Step5Summary form={form} />,
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <SEO title={language === "en" ? "Custom Song Generator – Create Your Personalized Song | Kalypso Music" : "Créer une Chanson Personnalisée en Ligne – Générateur IA | Kalypso Music"} description={language === "en" ? "Create a custom song online in 5 guided steps: your story, AI lyrics, musical style and realistic singing voice. The AI song generator made for gifting." : "Créez votre chanson personnalisée en ligne en 5 étapes guidées : votre histoire, paroles IA, style musical et voix chantée réaliste. Le générateur de chanson IA pensé pour offrir."} path="/create" />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid={WIZARD.container}>
        <h1 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">{t("wizard.title")}</h1>

        <div className="mt-8">
          <StepIndicator current={step} total={TOTAL_STEPS} />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                {steps[step]}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-between">
              <Button variant="outline" className="rounded-full" data-testid={WIZARD.prevBtn} onClick={handlePrev} disabled={step === 0}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t("common.back")}
              </Button>
              {step < TOTAL_STEPS - 1 ? (
                <Button className="rounded-full" data-testid={WIZARD.nextBtn} onClick={handleNext}>
                  {t("common.next")}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button className="rounded-full px-8" data-testid={WIZARD.submitBtn} onClick={handleSubmit} disabled={submitting}>
                  {submitting ? t("common.loading") : t("wizard.createOrderBtn")}
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <PriceSidebar form={form} pricing={pricing} />
            <PurchaseStepsReassurance />
          </div>
        </div>
      </main>

      <GuestCheckoutGate
        open={showGuestGate}
        error={guestGateError}
        setError={setGuestGateError}
        onLogin={handleLoginInstead}
        onGuestReady={() => {
          setShowGuestGate(false);
          createOrder();
        }}
      />
    </div>
  );
}
